import React, { useEffect, useState } from "react";
import SidebarNav from "../sidebar";
import { useParams, useHistory, Link } from "react-router-dom";
import { getTradingPairById, getOrderCoinDcx, cancelOpenOrders } from "../../../service/api";
import { Tag, Spin, Table, Modal } from "antd";
import { usePermission } from "../../../hooks/usePermission";
import { FiArrowLeft } from "react-icons/fi";
import moment from "moment";
import { toast } from "react-toastify";
import Select from "react-select";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import CustomPagination from "../Pagination/pagination";
import FilterPanel from "../commoncomponent/FilterPanel";
import TableToolbar from "../commoncomponent/TableToolbar";
import { useTableSettings } from "../../../hooks/useTableSettings";
import { triggerDownload } from "../../../config/downloadUtils";
import { sanitizeInput } from "../../../config/validation";

const formatDateTime = (val) =>
  val ? moment(val).format("DD/MM/YYYY HH:mm") : "—";

const STATUS_COLORS = {
  COMPLETED: "green", FILLED: "green",
  PENDING: "orange", PARTIALLY_FILLED: "orange",
  CANCELLED: "red", CANCELED: "red", REJECTED: "red",
  OPEN: "blue",
};

const orderTypeOptions = [
  { label: "Buy", value: "BUY" },
  { label: "Sell", value: "SELL" },
];

const orderStatusOptions = [
  { label: "Open", value: "OPEN" },
  { label: "Filled", value: "FILLED" },
  { label: "Cancelled", value: "CANCELLED" },
];

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 38,
    height: 40,
    minWidth: 130,
    borderRadius: 4,
    border: "1px solid #ced4da",
    fontSize: 14,
    backgroundColor: "#fff",
  }),
  indicatorSeparator: () => ({ display: "none" }),
};

// ── ALL_COLUMNS definition for useTableSettings ──────────────────────────────

const ALL_COLUMNS = [
  {
    key: "date",
    title: "Date",
    defaultVisible: true,
    render: (r) => formatDateTime(r?.createdAt),
  },
  {
    key: "user",
    title: "User",
    defaultVisible: true,
    render: (r) => {
      const name = [r?.user?.firstName, r?.user?.lastName].filter(Boolean).join(" ");
      return name || r?.user?._id || "—";
    },
  },
  {
    key: "email",
    title: "Email",
    defaultVisible: false,
    render: (r) => r?.user?.email || "—",
  },
  {
    key: "type",
    title: "Type",
    defaultVisible: true,
    render: (r) =>
      r?.orderType
        ? `${r.orderType}`
        : "—",
  },
  {
    key: "status",
    title: "Status",
    defaultVisible: true,
    render: (r) => {
      const s = r?.orderId?.orderStatus || r?.orderStatus;
      return s ? s.toUpperCase() : "—";
    },
  },
  {
    key: "amount",
    title: "Amount",
    defaultVisible: true,
    render: (r) =>
      r?.amount != null ? `${r.amount} ${r?.toCoin?.symbol ?? ""}`.trim() : "—",
  },
  {
    key: "price",
    title: "Price",
    defaultVisible: true,
    render: (r) =>
      r?.price != null ? `${r.price} ${r?.toCoin?.symbol ?? ""}`.trim() : "—",
  },
  {
    key: "quantity",
    title: "Quantity",
    defaultVisible: true,
    render: (r) =>
      r?.quantity != null
        ? `${Number(r.quantity).toPrecision(5)} ${r?.fromCoin?.symbol ?? ""}`.trim()
        : "—",
  },
];

// ── reusable pieces ──────────────────────────────────────────────────────────

const InfoIcon = ({ title }) => (
  <span
    title={title}
    style={{
      cursor: "help",
      marginLeft: 6,
      color: "#1677ff",
      fontSize: 10,
      fontWeight: 700,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      border: "1px solid #1677ff",
      borderRadius: "50%",
      width: 13,
      height: 13,
      lineHeight: 1,
      verticalAlign: "middle",
    }}
  >
    i
  </span>
);

const InfoRow = ({ label, tooltip, children, mono }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "9px 0", borderBottom: "1px solid #f0f0f0" }}>
    <span style={{ fontSize: 13, color: "#8c8c8c", fontWeight: 500, minWidth: 150 }}>
      {label}
      {tooltip && <InfoIcon title={tooltip} />}
    </span>
    <span style={{ fontSize: 13, color: "#262626", textAlign: "right", fontFamily: mono ? "monospace" : undefined, wordBreak: "break-all", maxWidth: "58%" }}>
      {children ?? "—"}
    </span>
  </div>
);

const Card = ({ title, icon, children, style }) => (
  <div style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 10, marginBottom: 20, overflow: "hidden", ...style }}>
    {title && (
      <div style={{ padding: "14px 20px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 8 }}>
        {icon && <span style={{ fontSize: 16 }}>{icon}</span>}
        <span style={{ fontSize: 14, fontWeight: 600, color: "#262626" }}>{title}</span>
      </div>
    )}
    <div style={{ padding: "4px 20px 12px" }}>{children}</div>
  </div>
);

const StatCard = ({ label, value, color }) => (
  <div style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 10, padding: "16px 20px", flex: "1 1 160px", minWidth: 0 }}>
    <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 6, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</div>
    <div style={{ fontSize: 16, fontWeight: 700, color: color || "#262626" }}>{value ?? "—"}</div>
  </div>
);

// ── main component ───────────────────────────────────────────────────────────

const TradingPairDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const { canUpdate } = usePermission();

  const [pair, setPair] = useState(null);
  const [pairLoading, setPairLoading] = useState(true);

  // cancel open orders
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);

  const [orders, setOrders] = useState([]);
  const [orderTotal, setOrderTotal] = useState(0);
  const [orderPage, setOrderPage] = useState(1);
  const [orderLoading, setOrderLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  // filters
  const [orderType, setOrderType] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const {
    pageSize,
    setPageSize,
    pageSizeOptions,
    visibleColumnKeys,
    toggleColumn,
    resetColumns,
    dynamicColumns,
  } = useTableSettings({
    storageKey: "trading-pair-detail-orders",
    allColumns: ALL_COLUMNS,
  });

  // fetch pair
  const fetchPair = () => {
    if (!id) return;
    setPairLoading(true);
    getTradingPairById(id)
      ?.then((res) => {
        const d = res?.data ?? res;
        setPair(Array.isArray(d) ? d[0] : d);
      })
      .finally(() => setPairLoading(false));
  };

  useEffect(() => { fetchPair(); }, [id]);

  // build order query params
  const buildOrderBody = (overrides = {}) => {
    const body = { pairId: id, page: orderPage, sizePerPage: pageSize, pagination: true };
    if (orderType) body.orderType = orderType.value;
    if (orderStatus) body.orderStatus = orderStatus.value;
    if (startDate && endDate) { body.startDate = startDate; body.endDate = endDate; }
    if (searchTerm) body.search = searchTerm;
    return { ...body, ...overrides };
  };

  const fetchOrders = (page = orderPage) => {
    if (!id) return;
    setOrderLoading(true);
    getOrderCoinDcx(buildOrderBody({ page }))
      .then((res) => {
        const d = res?.data ?? res;
        setOrders(d?.result ?? d?.docs ?? []);
        setOrderTotal(d?.total ?? 0);
      })
      .catch(() => {})
      .finally(() => setOrderLoading(false));
  };

  useEffect(() => {
    fetchOrders(orderPage);
  }, [id, orderPage, pageSize, orderType, orderStatus, startDate, endDate, searchTerm]);

  // reset to page 1 on filter/pageSize change
  useEffect(() => {
    setOrderPage(1);
  }, [pageSize, orderType, orderStatus, startDate, endDate, searchTerm]);

  const handleDownload = (format) => {
    setDownloadLoading(true);
    getOrderCoinDcx(buildOrderBody({ page: 1, pagination: false }))
      .then((res) => {
        const result = res?.data?.result ?? res?.data?.docs ?? [];
        if (!result.length) { toast.warning("Nothing to download", { toastId: 'nothing-to-download' }); return; }
        const cols = ALL_COLUMNS.filter((c) => visibleColumnKeys.includes(c.key));
        triggerDownload(format, result, cols, `pair-orders-${id}`);
      })
      .catch(() => toast.error("Download failed", { toastId: 'download-failed' }))
      .finally(() => setDownloadLoading(false));
  };

  const handleDateRangeChange = (_, picker) => {
    setStartDate(picker.startDate.format("YYYY-MM-DD"));
    setEndDate(picker.endDate.format("YYYY-MM-DD"));
  };

  const clearFilters = () => {
    setOrderType(null);
    setOrderStatus(null);
    setStartDate(null);
    setEndDate(null);
    setSearchTerm("");
  };

  const handleCancelOpenOrders = async () => {
    if (!cancelReason.trim()) return;
    setCancelLoading(true);
    try {
      const res = await cancelOpenOrders(id, cancelReason.trim());
      if (res?.status) {
        toast.success(res.message || "Bulk cancel complete", { toastId: "cancel-orders-success" });
        if (res?.data?.failed > 0) {
          toast.warning(
            `${res.data.failed} order(s) could not be cancelled — they may have been filled already.`,
            { toastId: "cancel-orders-warning" }
          );
        }
        fetchPair();
        setOrderPage(1);
        fetchOrders(1);
      } else {
        toast.error(res?.message || "Something went wrong", { toastId: "cancel-orders-error" });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Something went wrong", { toastId: "cancel-orders-error" });
    } finally {
      setCancelLoading(false);
      setCancelModalOpen(false);
      setCancelReason("");
    }
  };

  const pairSymbol = pair?.fromCoin?.symbol && pair?.toCoin?.symbol
    ? `${pair.fromCoin.symbol} / ${pair.toCoin.symbol}`
    : "—";

  const statusLabel = pair?.status === 1 ? "Active" : pair?.status === 0 ? "Inactive" : "Unknown";
  const statusColor = pair?.status === 1 ? "green" : pair?.status === 0 ? "red" : "default";

  // Column render overrides for columns that need JSX (Link, Tag)
  const columnOverrides = {
    user: (_, r) => {
      const name = [r?.user?.firstName, r?.user?.lastName].filter(Boolean).join(" ");
      const userId = r?.user?._id;
      return userId
        ? <Link to={`/admin/user/profile/${userId}`} style={{ color: "#1677ff", fontWeight: 500 }}>{name || userId}</Link>
        : name || "—";
    },
    type: (_, r) => r?.orderType
      ? <Tag color={r.orderType === "BUY" ? "green" : "red"}>{r.orderType}</Tag>
      : "—",
    status: (_, r) => {
      const s = r?.orderId?.orderStatus || r?.orderStatus;
      return s ? <Tag color={STATUS_COLORS[s?.toUpperCase()] || "default"}>{s.toUpperCase()}</Tag> : "—";
    },
  };

  const tableColumns = [
    ...dynamicColumns.map((col) =>
      columnOverrides[col.key] ? { ...col, render: columnOverrides[col.key] } : col
    ),
    {
      title: "Action",
      key: "__action",
      render: (_, r) => r?._id ? (
        <button
          className="btn btn-sm bg-primary-light"
          onClick={() => history.push(`/admin/order-detail/${r._id}`)}
        >
          View Order
        </button>
      ) : null,
    },
  ];

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid">

          {/* ── Header ── */}
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title">Trading Pair Detail</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item"><Link to="/admin">Dashboard</Link></li>
                  <li className="breadcrumb-item"><Link to="/admin/tradingPair">Trading Pairs</Link></li>
                  <li className="breadcrumb-item active">{pairSymbol}</li>
                </ul>
              </div>
              <div className="col-auto d-flex align-items-center gap-2">
                {canUpdate("trading-pair") && (
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => setCancelModalOpen(true)}
                    disabled={pairLoading}
                  >
                    Cancel All Open Orders
                  </button>
                )}
                <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1" onClick={() => history.goBack()}>
                  <FiArrowLeft /> Back
                </button>
              </div>
            </div>
          </div>

          {pairLoading ? (
            <div className="text-center py-5"><Spin size="large" /></div>
          ) : (
            <>
              {/* ── Stat cards ── */}
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 24 }}>
                <StatCard label="Pair" value={<span style={{ fontSize: 18 }}>{pairSymbol}</span>} color="#1677ff" />
                <StatCard label="Status" value={<Tag color={statusColor} style={{ fontSize: 13 }}>{statusLabel}</Tag>} />
                <StatCard label="Current Price" value={pair?.currentPrice != null ? `${pair.currentPrice} ${pair?.toCoin?.symbol ?? ""}`.trim() : "—"} color="#1a7a4a" />
                <StatCard label="High Price (24h)" value={pair?.high24h != null ? `${pair.high24h} ${pair?.toCoin?.symbol ?? ""}`.trim() : "—"} color="#16a34a" />
                <StatCard label="Low Price (24h)" value={pair?.low24h != null ? `${pair.low24h} ${pair?.toCoin?.symbol ?? ""}`.trim() : "—"} color="#dc2626" />
                <StatCard label="Min Trade" value={pair?.minTradeAmount ?? "—"} />
                <StatCard label="Max Trade" value={pair?.maxTradeAmount ?? "—"} />
                <StatCard label="Created" value={<span style={{ fontSize: 13 }}>{formatDateTime(pair?.createdAt)}</span>} />
              </div>

              <div className="row">
                {/* ── LEFT — pair + coin info ── */}
                <div className="col-lg-5">

                  <Card title="Pair Details" icon="🔄">
                    <InfoRow label="ID" mono>{pair?._id}</InfoRow>
                    <InfoRow label="Pair Symbol">{pairSymbol}</InfoRow>
                    <InfoRow label="Current Price">
                      {pair?.currentPrice != null ? `${pair.currentPrice} ${pair?.toCoin?.symbol ?? ""}`.trim() : "—"}
                    </InfoRow>
                    <InfoRow label="High Price (24h)">
                      {pair?.high24h != null ? `${pair.high24h} ${pair?.toCoin?.symbol ?? ""}`.trim() : "—"}
                    </InfoRow>
                    <InfoRow label="Low Price (24h)">
                      {pair?.low24h != null ? `${pair.low24h} ${pair?.toCoin?.symbol ?? ""}`.trim() : "—"}
                    </InfoRow>
                    <InfoRow label="From Coin Volume">
                      {pair?.fromCoinVolume != null ? `${pair.fromCoinVolume} ${pair?.fromCoin?.symbol ?? ""}`.trim() : "—"}
                    </InfoRow>
                    <InfoRow label="To Coin Volume">
                      {pair?.toCoinVolume != null ? `${pair.toCoinVolume} ${pair?.toCoin?.symbol ?? ""}`.trim() : "—"}
                    </InfoRow>
                    <InfoRow label="Volume (24h)">
                      {pair?.volume24h != null ? `${pair.volume24h} ${pair?.toCoin?.symbol ?? ""}`.trim() : "—"}
                    </InfoRow>
                    <InfoRow label="Change 1h">
                      {pair?.priceChange1h != null
                        ? <span style={{ color: pair.priceChange1h >= 0 ? "#16a34a" : "#dc2626" }}>{pair.priceChange1h}%</span>
                        : "—"}
                    </InfoRow>
                    <InfoRow label="Change 24h">
                      {pair?.priceChange24h != null
                        ? <span style={{ color: pair.priceChange24h >= 0 ? "#16a34a" : "#dc2626" }}>{pair.priceChange24h}%</span>
                        : "—"}
                    </InfoRow>
                    <InfoRow
                      label="Min Trade Amount"
                      tooltip={`Minimum quantity allowed per order, in the From Coin (${pair?.fromCoin?.symbol || "asset"}).`}
                    >
                      {pair?.minTradeAmount != null
                        ? `${pair.minTradeAmount} ${pair?.fromCoin?.symbol || ""}`.trim()
                        : "—"}
                    </InfoRow>
                    <InfoRow
                      label="Max Trade Amount"
                      tooltip={`Maximum quantity allowed per order, in the From Coin (${pair?.fromCoin?.symbol || "asset"}).`}
                    >
                      {pair?.maxTradeAmount != null
                        ? `${pair.maxTradeAmount} ${pair?.fromCoin?.symbol || ""}`.trim()
                        : "—"}
                    </InfoRow>
                    <InfoRow label="Status"><Tag color={statusColor}>{statusLabel}</Tag></InfoRow>
                    <InfoRow label="Category">
                      {pair?.pairCategoryData?.length > 0
                        ? pair.pairCategoryData.map((c) => c?.name).join(", ")
                        : null}
                    </InfoRow>
                    <InfoRow label="Created At">{formatDateTime(pair?.createdAt)}</InfoRow>
                    <InfoRow label="Updated At">{formatDateTime(pair?.updatedAt)}</InfoRow>
                  </Card>

                  <Card title="Trading Constraints" icon="📏">
                    <InfoRow
                      label="Base Currency Precision"
                      tooltip={`Number of decimal places allowed for order PRICE. Price is in the To Coin (${pair?.toCoin?.symbol || "quote"}).`}
                    >
                      {pair?.basePrecision != null
                        ? `${pair.basePrecision} decimals${pair?.toCoin?.symbol ? ` (price in ${pair.toCoin.symbol})` : ""}`
                        : "—"}
                    </InfoRow>
                    <InfoRow
                      label="Target Currency Precision"
                      tooltip={`Number of decimal places allowed for order QUANTITY. Quantity is in the From Coin (${pair?.fromCoin?.symbol || "asset"}).`}
                    >
                      {pair?.targetPrecision != null
                        ? `${pair.targetPrecision} decimals${pair?.fromCoin?.symbol ? ` (qty in ${pair.fromCoin.symbol})` : ""}`
                        : "—"}
                    </InfoRow>
                    <InfoRow
                      label="Min Notional Value"
                      tooltip={`Minimum total order value (price × quantity). Expressed in the To Coin (${pair?.toCoin?.symbol || "quote"}).`}
                    >
                      {pair?.minNotional != null && pair?.minNotional !== ""
                        ? `${pair.minNotional} ${pair?.toCoin?.symbol || ""}`.trim()
                        : "—"}
                    </InfoRow>
                    <InfoRow
                      label="Step Size / Quantity Steps"
                      tooltip={`Quantity must be a multiple of this step. Expressed in the From Coin (${pair?.fromCoin?.symbol || "asset"}).`}
                    >
                      {pair?.stepSize != null && pair?.stepSize !== ""
                        ? `${pair.stepSize} ${pair?.fromCoin?.symbol || ""}`.trim()
                        : "—"}
                    </InfoRow>
                    <InfoRow
                      label="Source"
                      tooltip="Whether these four values come from an admin override or are auto-synced from CoinDCX every 6 hours."
                    >
                      {pair?.precisionManualOverride
                        ? "Admin override"
                        : "Auto-synced from CoinDCX (6h)"}
                    </InfoRow>
                    <InfoRow label="Last Synced">
                      {pair?.precisionLastSyncedAt
                        ? formatDateTime(pair.precisionLastSyncedAt)
                        : "—"}
                    </InfoRow>
                    <InfoRow label="CoinDCX Name" mono>
                      {pair?.coindcxName || "—"}
                    </InfoRow>
                  </Card>

                  <Card title="From Coin" icon="🪙">
                    <InfoRow label="Name">{pair?.fromCoin?.name}</InfoRow>
                    <InfoRow label="Symbol">{pair?.fromCoin?.symbol}</InfoRow>
                    <InfoRow label="ID" mono>{pair?.fromCoin?._id}</InfoRow>
                    <InfoRow label="Price">{pair?.fromCoin?.price}</InfoRow>
                    <InfoRow label="Chain Address" mono>{pair?.fromCoin?.chain_address}</InfoRow>
                    <InfoRow label="Status">
                      {pair?.fromCoin?.status != null
                        ? <Tag color={pair.fromCoin.status === 1 ? "green" : "red"}>{pair.fromCoin.status === 1 ? "Active" : "Inactive"}</Tag>
                        : null}
                    </InfoRow>
                  </Card>

                  <Card title="To Coin" icon="🪙">
                    <InfoRow label="Name">{pair?.toCoin?.name}</InfoRow>
                    <InfoRow label="Symbol">{pair?.toCoin?.symbol}</InfoRow>
                    <InfoRow label="ID" mono>{pair?.toCoin?._id}</InfoRow>
                    <InfoRow label="Price">{pair?.toCoin?.price}</InfoRow>
                    <InfoRow label="Chain Address" mono>{pair?.toCoin?.chain_address}</InfoRow>
                    <InfoRow label="Status">
                      {pair?.toCoin?.status != null
                        ? <Tag color={pair.toCoin.status === 1 ? "green" : "red"}>{pair.toCoin.status === 1 ? "Active" : "Inactive"}</Tag>
                        : null}
                    </InfoRow>
                  </Card>
                </div>

                {/* ── RIGHT — orders with filters ── */}
                <div className="col-lg-7">
                  <div className="list-layout" style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                    {/* orders table card */}
                    <div className="card" style={{ flex: 1, minWidth: 0 }}>
                      <div className="card-body">
                        <div style={{ marginBottom: 8 }}>
                          <span style={{ fontSize: 14, fontWeight: 600 }}>
                            Orders for {pairSymbol}
                          </span>
                          {orderTotal > 0 && (
                            <span style={{ fontSize: 13, color: "#8c8c8c", marginLeft: 8 }}>
                              {orderTotal} total
                            </span>
                          )}
                        </div>

                        <TableToolbar
                          pageSize={pageSize}
                          setPageSize={setPageSize}
                          pageSizeOptions={pageSizeOptions}
                          total={orderTotal}
                          page={orderPage}
                          visibleColumnKeys={visibleColumnKeys}
                          allColumns={ALL_COLUMNS}
                          toggleColumn={toggleColumn}
                          resetColumns={resetColumns}
                          onDownload={handleDownload}
                          downloadLoading={downloadLoading}
                        />

                        <div className="table-responsive">
                          {orderLoading ? (
                            <div className="text-center py-4"><Spin /></div>
                          ) : (
                            <Table
                              size="small"
                              pagination={false}
                              rowKey={(r) => r?._id ?? r?.id}
                              dataSource={orders}
                              columns={tableColumns}
                              scroll={{ x: "max-content" }}
                              locale={{ emptyText: "No orders found for this pair." }}
                            />
                          )}
                          {orderTotal > pageSize && (
                            <CustomPagination
                              key={`pg-${pageSize}`}
                              totalTabs={orderTotal}
                              tabsPerPage={pageSize}
                              handleChange={(p) => setOrderPage(p)}
                              className={orderLoading ? "display-hide" : "display-flex"}
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* filter panel */}
                    <div style={{ width: 220, flexShrink: 0 }}>
                      <FilterPanel
                        count={orderTotal}
                        onClear={clearFilters}
                      >
                        <FilterPanel.Field label="Search">
                          <input
                            type="text"
                            placeholder="User name or email"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(sanitizeInput(e.target.value))}
                          />
                        </FilterPanel.Field>

                        <FilterPanel.Field label="Order Type">
                          <Select
                            options={orderTypeOptions}
                            value={orderType}
                            onChange={setOrderType}
                            styles={selectStyles}
                            placeholder="Any"
                            isClearable
                          />
                        </FilterPanel.Field>

                        <FilterPanel.Field label="Order Status">
                          <Select
                            options={orderStatusOptions}
                            value={orderStatus}
                            onChange={setOrderStatus}
                            styles={selectStyles}
                            placeholder="Any"
                            isClearable
                          />
                        </FilterPanel.Field>

                        <FilterPanel.Field label="Date Range">
                          <DateRangePicker onApply={handleDateRangeChange} isClearable>
                            <input
                              type="text"
                              placeholder="Select date range"
                              readOnly
                              style={{ width: "100%", padding: "6px 10px", border: "1px solid #ced4da", borderRadius: 4, fontSize: 13, cursor: "pointer" }}
                            />
                          </DateRangePicker>
                        </FilterPanel.Field>
                      </FilterPanel>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <Modal
        open={cancelModalOpen}
        onCancel={() => { if (!cancelLoading) { setCancelModalOpen(false); setCancelReason(""); } }}
        title={<span style={{ color: "#dc2626" }}>⚠️ Cancel All Open Orders</span>}
        footer={[
          <button
            key="cancel"
            className="btn btn-sm btn-outline-secondary me-2"
            onClick={() => { setCancelModalOpen(false); setCancelReason(""); }}
            disabled={cancelLoading}
          >
            Cancel
          </button>,
          <button
            key="confirm"
            className="btn btn-sm btn-danger"
            onClick={handleCancelOpenOrders}
            disabled={cancelLoading || !cancelReason.trim()}
          >
            {cancelLoading ? <Spin size="small" /> : "Confirm"}
          </button>,
        ]}
        maskClosable={!cancelLoading}
        closable={!cancelLoading}
        destroyOnClose
      >
        <p style={{ marginBottom: 12 }}>
          This will cancel <strong>all open orders</strong> for{" "}
          <strong>{pairSymbol}</strong>. This action cannot be undone.
        </p>
        <div style={{ marginBottom: 4, fontSize: 13, fontWeight: 500 }}>
          Reason <span style={{ color: "#dc2626" }}>*</span>
        </div>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Enter reason for cancelling orders"
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          disabled={cancelLoading}
          maxLength={300}
          autoFocus
        />
      </Modal>
    </>
  );
};

export default TradingPairDetail;
